import * as Sentry from '@sentry/react';
import ENV, { getEnvironment } from '../constants/Env';

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Determine whether errors should be reported to Sentry.
 *
 * @returns {boolean} Whether errors should be reported to Sentry.
 */
function shouldReportErrors() {
  return getEnvironment() !== 'localhost';
}

/**
 * Initialize logging with Sentry.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/logs
 * 
 * Fine-grained debugging
 * Sentry.logger.trace("Entering function", { fn: "processOrder" }); 
 * 
 * Development diagnostics
 * Sentry.logger.debug("Cache lookup", { key: "user:123" });
 * 
 * Normal operations, milestones
 * Sentry.logger.info("Order created", { orderId: "order_456" });
 * 
 * Potential issues, degraded state
 * Sentry.logger.warn("Rate limit approaching", { current: 95, max: 100 });
 * 
 * Failures that need attention
 * Sentry.logger.error("Payment failed", { reason: "card_declined" });
 * 
 * Critical failures, system down
 * Sentry.logger.fatal("Database unavailable", { host: "primary" });
 */
export function initLogging() {
  if (shouldReportErrors()) {
    const environment = getEnvironment();

    Sentry.init({
      dsn: ENV.sentryDsn,
      environment,
      // This comes from Heroku automatically.
      // https://devcenter.heroku.com/articles/dyno-metadata
      release: process.env.HEROKU_SLUG_COMMIT,
      enableLogs: true,
    });
  }
}

/**
 * Capture an error with Sentry.
 *
 * @param error An error object or a string message.
 */
export function captureException(error: any) {
  console.error(error);

  if (shouldReportErrors()) {
    Sentry.captureException(error instanceof Error ? error : new Error(error));
  }
}

/**
 * Capture a message with Sentry.
 *
 * @param message The message to log.
 * @param extra (Optional) Extra context to provide to the Sentry message.
 */
export function captureMessage(
  message: string,
  extra?: Record<string, unknown>,
) {
  if (extra) {
    console.log(message, extra);
  } else {
    console.log(message);
  }

  if (shouldReportErrors()) {
    const context = extra ? { extra } : undefined;
    Sentry.captureMessage(message, context);
  }
}

/**
 * Capture a log line with optional data
 *
 * @param level The log level (e.g., 'debug', 'info', 'warn', 'error', 'fatal').
 * @param message The message to log.
 * @param logData (Optional) Key-value pairs to provide to the Sentry log.
 */
export function captureLog(level: LogLevel, message: string, logData?: Record<string, unknown>) {
  if (shouldReportErrors()) {
    Sentry.logger[level](message, logData);
  }
}
